/**
 * Coups de pouce — Attendu 1068 « Reconnaitre un cube, un pavé représenté en
 * perspective cavalière ».
 *
 * nature_qcm_unique + nature_planche_qcm_unique + nature_tableau_vf : entrée
 * PARTAGÉE — compter les faces et lire leur forme, ne pas se fier aux
 * parallélogrammes (faces fuyantes), regarder la face de devant en vraie
 * grandeur, l'inclusion cube ⊂ pavé, les trois pointillés. L'exemple (revue
 * 2026-09-05) : un cube et un pavé côte à côte, face de devant puis faces
 * fuyantes surlignées. SVG piloté par les flèches de la sheet.
 *
 * Sans coup de pouce (pur rappel) : lecture_qcm_unique, vf_tableau_vf.
 */

const TRAIT = 'stroke="#1E3A8A" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"';
const POINT = 'stroke="#1E3A8A" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" stroke-dasharray="5 4"';

// cube : face avant 48 × 48, fuyante (+24, −20) ; pavé : face avant 70 × 40, même fuyante
const CUBE = `
<polygon points="0,0 48,0 72,-20 24,-20" fill="#dbeafe" ${TRAIT}/>
<polygon points="48,0 48,48 72,28 72,-20" fill="#dbeafe" ${TRAIT}/>
<polygon points="0,0 48,0 48,48 0,48" fill="#eff6ff" ${TRAIT}/>
<line x1="24" y1="28" x2="0" y2="48" ${POINT}/><line x1="24" y1="28" x2="72" y2="28" ${POINT}/><line x1="24" y1="28" x2="24" y2="-20" ${POINT}/>`;
const PAVE = `
<polygon points="0,0 70,0 94,-20 24,-20" fill="#dbeafe" ${TRAIT}/>
<polygon points="70,0 70,40 94,20 94,-20" fill="#dbeafe" ${TRAIT}/>
<polygon points="0,0 70,0 70,40 0,40" fill="#eff6ff" ${TRAIT}/>
<line x1="24" y1="20" x2="0" y2="40" ${POINT}/><line x1="24" y1="20" x2="94" y2="20" ${POINT}/><line x1="24" y1="20" x2="24" y2="-20" ${POINT}/>`;

const SVG_CUBE_PAVE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Reconnaître un cube et un pavé en perspective cavalière, en 3 étapes à faire défiler avec les flèches. Deux solides dessinés avec leurs arêtes cachées en pointillés. Étape 2 : seule la face de devant est en vraie grandeur, un carré à gauche, un rectangle à droite. Étape 3 : les faces fuyantes sont dessinées penchées mais sont des carrés ou des rectangles en réalité ; 6 faces chacun, 3 pointillés chacun. À gauche un cube, à droite un pavé, et le cube est aussi un pavé">
  <style>
    @keyframes cp1068-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1068-pop { opacity: 0; animation: cp1068-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Lequel est le cube, lequel est le pavé ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">La face de devant, en vraie grandeur</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Les faces fuyantes, dessinées penchées</text>

  <!-- ── Les deux solides (étape 1) ─────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <g transform="translate(34,62)">${CUBE}</g>
    <g transform="translate(196,70)">${PAVE}</g>
    <text x="70" y="128" font-size="13" font-weight="800" fill="#1E3A8A" text-anchor="middle">①</text>
    <text x="243" y="128" font-size="13" font-weight="800" fill="#1E3A8A" text-anchor="middle">②</text>
  </g>

  <!-- ── La face de devant (étape 2) ────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1068-pop">
      <polygon points="34,62 82,62 82,110 34,110" fill="#1D9E75" fill-opacity="0.3" stroke="#1D9E75" stroke-width="2"/>
      <text x="58" y="90" font-size="10.5" font-weight="800" fill="#0f5c44" text-anchor="middle">carré</text>
      <polygon points="196,70 266,70 266,110 196,110" fill="#1D9E75" fill-opacity="0.3" stroke="#1D9E75" stroke-width="2"/>
      <text x="231" y="94" font-size="10.5" font-weight="800" fill="#0f5c44" text-anchor="middle">rectangle</text>
      <text x="8" y="150" font-size="11.5" font-weight="700" fill="#1D9E75">Seule la face de devant est en vraie grandeur :</text>
      <text x="8" y="165" font-size="11.5" font-weight="700" fill="#1D9E75">un carré à gauche, un rectangle à droite.</text>
    </g>
  </g>

  <!-- ── Les faces fuyantes (étape 3) ───────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1068-pop">
      <polygon points="34,62 82,62 106,42 58,42" fill="#B45309" fill-opacity="0.3" stroke="#B45309" stroke-width="2"/>
      <polygon points="82,62 82,110 106,90 106,42" fill="#B45309" fill-opacity="0.3" stroke="#B45309" stroke-width="2"/>
      <polygon points="196,70 266,70 290,50 220,50" fill="#B45309" fill-opacity="0.3" stroke="#B45309" stroke-width="2"/>
      <polygon points="266,70 266,110 290,90 290,50" fill="#B45309" fill-opacity="0.3" stroke="#B45309" stroke-width="2"/>
      <text x="8" y="188" font-size="11.5" font-weight="700" fill="#B45309">Les faces fuyantes sont dessinées penchées :</text>
      <text x="8" y="203" font-size="11.5" font-weight="700" fill="#B45309">en réalité, des carrés ou des rectangles.</text>
      <text x="8" y="222" font-size="11.5" font-weight="700" fill="#1E3A8A">6 faces chacun, 3 pointillés chacun.</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">① cube, ② pavé. Et le cube est aussi un pavé.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_NATURE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Compte les faces et regarde leur forme.** Un cube et un pavé ont 6 faces, toutes rectangulaires ou carrées. Un triangle visible, une base ronde ou une pointe : ce n'est ni l'un ni l'autre.",
                "**Ne te fie pas aux parallélogrammes.** En perspective cavalière, les faces qui partent vers l'arrière sont dessinées penchées : elles restent des rectangles ou des carrés dans la réalité.",
                "**Seule la face de devant est en vraie grandeur.** C'est elle qu'on regarde : un carré devant et trois dimensions égales, c'est un cube ; un rectangle devant, c'est un pavé qui n'est pas un cube.",
                '**Un cube est un pavé particulier.** « Est-ce un pavé ? » est vrai pour un cube ; mais le nom le plus précis d\'un cube est « cube ».',
                '**Les pointillés** sont les arêtes cachées : 3 pour un cube ou un pavé, elles partent du sommet arrière.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_CUBE_PAVE },
    ],
};

const CP_1068 = {
    nature_qcm_unique: AIDE_NATURE,
    nature_planche_qcm_unique: AIDE_NATURE,
    nature_tableau_vf: AIDE_NATURE,
};

export default CP_1068;
